import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { LinkAdAccountsModalPayload, SelectAdChannelModalPayload } from '@hooks/useAdAccount'
import useNumericParams from '@hooks/useNumericParams'
import useModals from '@hooks/useModals'
import useSetState from '@hooks/useSetState'

import LinkAdAccountsModal from '@modals/LinkAdAccountsModal'
import SelectAdChannelModal from '@modals/SelectAdChannelModal'

import PageContent from './components/PageContent'

const defaultState = {
  showLinkAdAccountsModal: false,
  showSelectAdChannelModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    LinkAdAccountsModal: {
      closeModal: () => setState({ showLinkAdAccountsModal: false }),
      createAndLinkAdAccounts: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    SelectAdChannelModal: {
      closeModal: () => setState({ showSelectAdChannelModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const { showLinkAdAccountsModal, showSelectAdChannelModal } = state

  const { adAccountGroupId } = useNumericParams()

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showLinkAdAccountsModal: (payload: LinkAdAccountsModalPayload) => {
        setModalData('LinkAdAccountsModal', payload)
        setState({ showLinkAdAccountsModal: true })
      },
      showSelectAdChannelModal: (payload: SelectAdChannelModalPayload) => {
        setModalData('SelectAdChannelModal', payload)
        setState({ showSelectAdChannelModal: true })
      },
      updateAdAccountGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent adAccountGroupId={adAccountGroupId} />

        <LinkAdAccountsModal
          callbacks={callbacks('LinkAdAccountsModal', setState)}
          showModal={showLinkAdAccountsModal}
        />

        <SelectAdChannelModal
          callbacks={callbacks('SelectAdChannelModal', setState)}
          showModal={showSelectAdChannelModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
